import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Calendar, CreditCard, AlertCircle, Sparkles, TriangleAlert } from "lucide-react";

interface StripeSubscription {
	subscription: {
		id: string;
		status: string;
		currency: string;
		quantity: number;
		current_period_start: number;
		current_period_end: number;
		cancel_at_period_end: boolean;
        billing_cycle_anchor: number;
		trial_start?: number;
		trial_end?: number;
		discounts: string[];
		items: {
			data: Array<{
                current_period_start: number;
                current_period_end: number;
				id: string;
				quantity: number;
				price: {
					id: string;
					unit_amount: number;
					currency: string;
					recurring: {
						interval: string;
						interval_count: number;
					};
					lookup_key: string;
					product: string;
				};
				plan: {
					id: string;
					amount: number;
					currency: string;
					interval: string;
					interval_count: number;
					product: string;
				};
			}>;
		};
		plan: {
			id: string;
			amount: number;
			currency: string;
			interval: string;
			interval_count: number;
			product: string;
		};
	};
}

interface StripePlan {
	product_id: string;
	product_name: string;
	description: string | null;
	price_id: string;
	currency: string;
	unit_amount: number;
	product_attrs: {
		id: string;
		name: string;
		type: string;
		active: boolean;
		description: string | null;
		default_price: string;
	};
	price_attrs: {
		id: string;
		type: string;
		active: boolean;
		currency: string;
		recurring: {
			interval: string;
			interval_count: number;
			usage_type: string;
		};
		lookup_key: string;
		unit_amount: number;
		unit_amount_decimal: string;
	};
}

interface CurrentSubscriptionProps {
	subscription: StripeSubscription;
	availablePlans: StripePlan[];
	isLoading: boolean;
	onUpdateClick: () => void;
	onCancelSubscription: () => Promise<void>;
	isCancelling: boolean;
}

const CANCEL_CONFIRM_PHRASE = "cancel digger subscription";

export const CurrentSubscription = ({
	subscription,
	availablePlans,
	isLoading,
	onUpdateClick,
	onCancelSubscription,
	isCancelling,
}: CurrentSubscriptionProps) => {
	const { t } = useTranslation();
	const [showCancelConfirm, setShowCancelConfirm] = useState(false);
	const [cancelConfirmText, setCancelConfirmText] = useState("");

	const formatPrice = (amount: number, currency: string) => {
		return new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: currency.toUpperCase(),
		}).format(amount / 100);
	};
	const formatDate = (timestamp: number) => {
		if (!timestamp || isNaN(timestamp)) {
			return "N/A";
		}
		const date = new Date(timestamp * 1000);
		if (isNaN(date.getTime())) {
			return "N/A";
		}
		return new Intl.DateTimeFormat("en-US", {
			year: "numeric",
			month: "short",
			day: "numeric",
		}).format(date);
	};

	const getStatusBadge = (status: string) => {
		switch (status.toLowerCase()) {
			case "active":
				return <Badge className="bg-green-100 text-green-800">Active</Badge>;
			case "trialing":
				return <Badge className="bg-blue-100 text-blue-800">Free Trial</Badge>;
			case "canceled":
				return <Badge variant="destructive">Canceled</Badge>;
			case "past_due":
				return <Badge className="bg-yellow-100 text-yellow-800">Past Due</Badge>;
			default:
				return <Badge variant="outline">{status}</Badge>;
		}
	};

	// Find the product name from available plans
	const currentPlan = availablePlans.find(
		plan => plan.price_id === subscription.subscription.plan.id
	);
	if (isLoading) {
		return (
			<Card>
				<CardHeader>
					<CardTitle>Loading Subscription...</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="flex items-center justify-center py-8">
						<div className="text-sm text-muted-foreground">Loading subscription details...</div>
					</div>
				</CardContent>
			</Card>
		);
	}

	if (!subscription?.subscription) {
		return (
			<Card>
				<CardHeader>
					<CardTitle>No Subscription Data</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="flex items-center justify-center py-8">
						<div className="text-sm text-muted-foreground">Unable to load subscription details.</div>
					</div>
				</CardContent>
			</Card>
		);
	}
	return (
		<div className="space-y-6">
			{/* Current Subscription */}
			<Card>
				<CardHeader>
					<div className="flex items-center justify-between">
						<div>
							<CardTitle>Current Subscription</CardTitle>
							<CardDescription>
								Manage your subscription and billing
							</CardDescription>
						</div>
						{getStatusBadge(subscription.subscription.status)}
					</div>
				</CardHeader>
				<CardContent>
					<div className="grid gap-6">
						{/* Plan Details */}
						<div className="space-y-2">
							<div className="text-lg font-semibold">
								{currentPlan?.product_name || "Unknown Plan"}
							</div>
							<div className="text-sm text-muted-foreground">
								{currentPlan?.description || ""}
							</div>							
                            <div className="flex items-center gap-2 text-2xl font-bold">
								{formatPrice(subscription.subscription.plan?.amount || 0, subscription.subscription.currency)}
								<span className="text-sm font-normal text-muted-foreground">
									/ {subscription.subscription.plan?.interval || "month"}
								</span>
							</div>
						</div>

						{/* Subscription Details */}
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">							
                            <div className="space-y-2">
								<Label className="text-sm font-medium">Quantity</Label>
								<div className="flex items-center gap-2">
									<CreditCard className="h-4 w-4 text-muted-foreground" />
									<span>{subscription.subscription.quantity || 0} licenses</span>
								</div>
							</div>

							<div className="space-y-2">
								<Label className="text-sm font-medium">Billing Cycle</Label>
								<div className="flex items-center gap-2">
									<Calendar className="h-4 w-4 text-muted-foreground" />
									<span className="capitalize">{subscription.subscription.plan?.interval || "monthly"}</span>
								</div>
							</div>

							<div className="space-y-2">
								<Label className="text-sm font-medium">Current Period</Label>
								<div className="text-sm">
									{formatDate(subscription.subscription.items.data[0].current_period_start)} - {" "}
									{formatDate(subscription.subscription.items.data[0].current_period_end)}
								</div>
							</div>

							<div className="space-y-2">
								<Label className="text-sm font-medium">Next Billing Date</Label>
								<div className="text-sm">
									{formatDate(subscription.subscription.billing_cycle_anchor)}
								</div>
							</div>
						</div>						{/* Discounts */}
						{subscription.subscription.discounts && subscription.subscription.discounts.length > 0 && (
							<div className="space-y-2">
								<Label className="text-sm font-medium">Active Discounts</Label>
								<div className="flex flex-wrap gap-2">
									{subscription.subscription.discounts.map((discount, index) => (
										<Badge key={index} variant="secondary">
											Discount Applied
										</Badge>
									))}
								</div>
							</div>
						)}

						{/* Free Trial Banner */}
						{subscription.subscription.status === "trialing" && subscription.subscription.trial_end && (
							<div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-sm">
								<Sparkles className="h-4 w-4 text-blue-600 shrink-0" />
								<div className="text-sm text-blue-800">
									You are currently on a free trial. Your trial ends on{" "}
									<strong>{formatDate(subscription.subscription.trial_end)}</strong>{" "}
									({Math.max(0, Math.ceil((subscription.subscription.trial_end * 1000 - Date.now()) / 86400000))} days remaining).
									You will not be charged until then.
								</div>
							</div>
						)}

						{/* Cancellation Warning */}
						{subscription.subscription.cancel_at_period_end && (
							<div className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-sm">
								<AlertCircle className="h-4 w-4 text-yellow-600" />
								<div className="text-sm text-yellow-800">
									Your subscription will cancel at the end of the current period
								</div>
							</div>
						)}
					</div>
				</CardContent>
				<CardFooter className="flex justify-between">
					<Button
						onClick={onUpdateClick}
						className="bg-digger-blue hover:bg-digger-blue-light"
					>
						Update Plan
					</Button>
				</CardFooter>
            </Card>

			{/* Danger Zone */}
			<Card className="border-red-300">
				<CardHeader>
					<div className="flex items-center gap-2">
						<TriangleAlert className="h-5 w-5 text-red-600" />
						<CardTitle className="text-red-600">{t("admin.billing.dangerZone.title")}</CardTitle>
					</div>
					<CardDescription>{t("admin.billing.dangerZone.description")}</CardDescription>
				</CardHeader>
				<CardContent>
					{!showCancelConfirm ? (
						<Button
							variant="destructive"
							onClick={() => setShowCancelConfirm(true)}
						>
							{t("admin.billing.dangerZone.cancelSubscription")}
						</Button>
					) : (
						<div className="space-y-4 p-4 border border-red-300 rounded-sm bg-red-50">
							<div className="space-y-2">
								<p className="text-sm font-semibold text-red-800">{t("admin.billing.dangerZone.consequences.title")}</p>
								<ul className="text-sm text-red-700 space-y-1 list-disc list-inside">
									<li>{t("admin.billing.dangerZone.consequences.noAccess")}</li>
									<li>{t("admin.billing.dangerZone.consequences.jobsDisabled")}</li>
									<li>{t("admin.billing.dangerZone.consequences.dataDeleted")}</li>
								</ul>
							</div>
							<div className="space-y-2">
								<Label className="text-sm text-red-800">
									{t("admin.billing.dangerZone.confirm.instruction", { phrase: CANCEL_CONFIRM_PHRASE })}
								</Label>
								<Input
									value={cancelConfirmText}
									onChange={(e) => setCancelConfirmText(e.target.value)}
									placeholder={CANCEL_CONFIRM_PHRASE}
									className="border-red-300 focus-visible:ring-red-400"
								/>
							</div>
							<div className="flex gap-2">
								<Button
									variant="destructive"
									disabled={cancelConfirmText !== CANCEL_CONFIRM_PHRASE || isCancelling}
									onClick={async () => {
										await onCancelSubscription();
										setShowCancelConfirm(false);
										setCancelConfirmText("");
									}}
								>
									{isCancelling ? t("admin.billing.dangerZone.confirm.cancelling") : t("admin.billing.dangerZone.confirm.button")}
								</Button>
								<Button
									variant="outline"
									onClick={() => {
										setShowCancelConfirm(false);
										setCancelConfirmText("");
									}}
									disabled={isCancelling}
								>
									{t("admin.billing.dangerZone.confirm.cancel")}
								</Button>
							</div>
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
};
